import { RedirectRequest } from '@azure/msal-browser'
import { useMsal } from '@azure/msal-react'
import { yupResolver } from '@hookform/resolvers/yup'
import { ControlledPhoneField, ControlledSelect, ControlledTextField } from '@if/mui'
import { Alert, Dialog, DialogActions, DialogContent, DialogTitle, Grid, MenuItem } from '@mui/material'
import { isFederatedUser, loginRequest } from '@src/authConfig'
import AsyncActionButton from '@src/components/AsyncActionButton'
import PageSpinner from '@src/components/PageSpinner'
import DialogCloseButton from '@src/components/common/DialogCloseButton'
import { useUser } from '@src/contexts/AppContext'
import { useMerchantById } from '@src/data/api/merchants-api/merchants-api'
import { useGetUserProfile, useUpdateUserProfile } from '@src/data/api/user-api/user-api'
import { FinancingCompanyConfigs } from '@src/types/Constants'
import { User } from '@src/types/User'
import { EditProfileInformation, EditProfileInformationSchema } from '@src/types/profileSchema'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { Trans, useTranslation } from 'react-i18next'

interface Props {
  open: boolean
  handleClose: () => void
}

const EditProfileDialog = ({ open, handleClose }: Props) => {
  const { t } = useTranslation()
  const { instance, accounts } = useMsal()
  const userToken = useUser()
  // Iceberg employees enter MerchantDashboard via the B2C IdP federation policy
  // (Phase 2D). Their profile is managed in Entra ID Iceberg, not B2C, so the
  // dialog must be read-only when the active MSAL account uses that policy.<>
  const isInternalUser = isFederatedUser(accounts[0])
  const [user, isLoadingUser] = useGetUserProfile(userToken?.id, isInternalUser)
  const [merchant] = useMerchantById(userToken?.merchantId ?? '')


  const {
    handleSubmit,
    reset,
    control: formControl,
  } = useForm<EditProfileInformation>({
    mode: 'onBlur',
    resolver: yupResolver(EditProfileInformationSchema),
  })

  useEffect(() => {
    if (user) {
      reset(user)
    }
  }, [reset, user])

  const [updateUser, isUpdatingUser, isUpdatingUserError, resetUpdateUser] = useUpdateUserProfile()

  const onSubmit = async (data: EditProfileInformation) => {
    if (user && data) {
      await updateUser({
        ...user,
        ...data,
        phone: data.phone ?? '',
      }).then((cUser: User) => {
        resetUpdateUser()
        if (cUser) handleClose()
      })
    }
  }

  const handlePasswordReset = async () => {
    await instance.loginRedirect({
      ...loginRequest,
      authority: `${import.meta.env.VITE_B2C_AUTHORITY}${import.meta.env.VITE_B2C_RESET_POLICY}`,
    } as RedirectRequest)
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      maxWidth="md"
    >
      <DialogTitle id="alert-dialog-title">
        {t('profile.title')}
        <DialogCloseButton onClose={handleClose} />
      </DialogTitle>
      <DialogContent>
        <PageSpinner isLoading={isLoadingUser} />
        {isInternalUser && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Trans
              i18nKey="profile.viewingAsInternalUser"
              values={{
                merchantName: merchant?.name ?? '',
                companyName: merchant?.financingCompanyId
                  ? FinancingCompanyConfigs[merchant.financingCompanyId].name
                  : '',
              }}
              components={{ strong: <strong /> }}
            />
          </Alert>
        )}
        <Grid container spacing={2} sx={{ mt: 1 }} justifyContent="center">
          <Grid
            size={{
              sm: 12,
            }}
          >
            <ControlledSelect
              control={formControl}
              name="language"
              fullWidth
              disableEmptyOption
              label={t('common.language')}
              defaultValue="en"
              disabled={isInternalUser}
            >
              <MenuItem value="en">{t('common.english')}</MenuItem>
              <MenuItem value="fr">{t('common.french')}</MenuItem>
            </ControlledSelect>
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6,
            }}
          >
            <ControlledTextField
              id="firstName"
              name="firstName"
              control={formControl}
              label={t('common.firstName')}
              slotProps={{ htmlInput: { maxLength: 50 } }}
              required
              fullWidth
              disabled={isInternalUser}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6,
            }}
          >
            <ControlledTextField
              id="lastName"
              name="lastName"
              control={formControl}
              label={t('common.lastName')}
              slotProps={{ htmlInput: { maxLength: 50 } }}
              required
              fullWidth
              disabled={isInternalUser}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6,
            }}
          >
            <ControlledTextField
              id="jobTitle"
              name="jobTitle"
              control={formControl}
              label={t('common.jobTitle')}
              slotProps={{ htmlInput: { maxLength: 100 } }}
              required
              fullWidth
              disabled={isInternalUser}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6,
            }}
          >
            <ControlledPhoneField
              control={formControl}
              name="phone"
              label={t('common.phone')}
              fullWidth
              id="cellPhone"
              disabled={isInternalUser}
            />
          </Grid>
        </Grid>
      </DialogContent>
      {!isInternalUser && (
        <DialogActions>
          <Grid container spacing={1} justifyContent="right" textAlign="center" marginRight={2}>
            <Grid
              hidden
              size={{
                xs: 12,
                md: 4,
              }}
            >
              <AsyncActionButton fullWidth variant="contained" disabled>
                {t('profile.changeSecurityQuestion')}
              </AsyncActionButton>
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 4,
              }}
            >
              <AsyncActionButton fullWidth variant="contained" onClick={handlePasswordReset} disabled={isUpdatingUser}>
                {t('profile.changePassword')}
              </AsyncActionButton>
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 4,
              }}
            >
              <AsyncActionButton
                fullWidth
                variant="contained"
                isPending={isUpdatingUser}
                isError={isUpdatingUserError}
                onClick={handleSubmit(onSubmit)}
              >
                {t('profile.updateInformation')}
              </AsyncActionButton>
            </Grid>
          </Grid>
        </DialogActions>
      )}
    </Dialog>
  )
}

export default EditProfileDialog
